import React, { useState, useEffect } from "react";

// Original prices for Black Friday comparison
const originalPrices = {
    premium: {
        months: [
            { time: 1, cost: 60 },
            { time: 3, cost: 145 },
            { time: 6, cost: 240 },
            { time: 12, cost: 360 },
        ],
    },
    basic: {
        months: [
            {
                time: 1,
                options: [
                    { entries: 8, cost: 35 },
                ]
            },
            {
                time: 3,
                options: [
                    { entries: 24, cost: 85 },
                ]
            },
                {
                    time: 12,
                    options: [
                        { entries: 96, cost: 340 },
                    ]
                }
        ],
    },
};

export const IndividualDiscountCard = ({ formData, setFormData, discountData }) => {
    // Set default selection to 1 month (but no option selected for basic membership)
    const defaultMonth = discountData?.months.find(month => month.time === 1);

    const [dropDown, setDropDown] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(defaultMonth || discountData?.months[0]);
    const [selectedOption, setSelectedOption] = useState(null); // No default option - user must select
    const [optionsDropDown, setOptionsDropDown] = useState(false);

    // Calculate entries based on plan type
    const calculateEntries = (monthTime) => {
        if (discountData.entries === "unlimited") return "unlimited";
        if (discountData.entries === "variable") return ""; // Return empty to avoid showing "variable"
        const baseEntriesPerMonth = parseInt(discountData.entries, 10);
        return baseEntriesPerMonth * monthTime;
    };

    // Handle choosing a plan and setting the cost and entries
    const handleChosePlan = () => {
        if (selectedMonth.options && !selectedOption) {
            alert("Please select an entry option."); // Prevent setting if no option is selected
            return;
        }

        const calculatedEntries = calculateEntries(selectedMonth.time);
        const cost = selectedOption ? selectedOption.cost : selectedMonth.cost;
        const entries = selectedOption ? selectedOption.entries : calculatedEntries;

        setFormData((prevData) => ({
            ...prevData,
            time: selectedMonth.time,
            totalAmount: cost,
            payableAmount: cost,
            entries: entries,
            premium: discountData.entries === "unlimited",
        }));
    };

    // Sync internal state with formData when it changes externally (e.g., from image click)
    useEffect(() => {
        if (formData.type === "Individual" && formData.time && formData.entries) {
            // Update selectedMonth based on formData.time
            const matchingMonth = discountData?.months.find(month => month.time === formData.time);
            if (matchingMonth && selectedMonth.time !== matchingMonth.time) {
                setSelectedMonth(matchingMonth);
            }
            
            // Update selectedOption if it's a basic plan with options
            if (matchingMonth && matchingMonth.options && formData.time === matchingMonth.time) {
                const matchingOption = matchingMonth.options.find(opt => opt.entries === formData.entries);
                if (matchingOption && (!selectedOption || selectedOption.entries !== matchingOption.entries)) {
                    setSelectedOption(matchingOption);
                }
            } else if (matchingMonth && !matchingMonth.options) {
                // If no options, clear selectedOption
                setSelectedOption(null);
            }
        }
    }, [formData.time, formData.entries, formData.type, discountData, selectedMonth.time, selectedOption]);

    // Check if the current selection is already chosen
    const isSelected = () => {
        return (
            formData.time === selectedMonth.time &&
            formData.totalAmount === (selectedOption ? selectedOption.cost : selectedMonth.cost) &&
            formData.entries === (selectedOption ? selectedOption.entries : calculateEntries(selectedMonth.time))
        );
    };

    return (
        <div className="flex flex-col gap-5 border-2 border-gray-200 rounded-2xl min-w-[320px] p-6 max-h-fit h-fit font-custom2 bg-white shadow-lg hover:shadow-xl transition-shadow duration-300">
            {/* Month Dropdown */}
            <div className="flex flex-col gap-2 rounded-xl relative w-full">
                <button
                    onClick={() => setDropDown(!dropDown)}
                    id="dropdownDefaultButton"
                    data-dropdown-toggle="dropdown"
                    className="text-blue-700 bg-blue-50 border border-blue-200 font-semibold rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center justify-between w-full hover:bg-blue-100 transition-colors"
                    type="button"
                >
                    {selectedMonth?.time} muaj
                    <svg className="w-2.5 h-2.5 ms-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 4 4 4-4" />
                    </svg>
                </button>

                <div id="dropdown" className={`z-10 bg-white text-black top-12 absolute divide-y divide-gray-100 rounded-lg shadow-lg border border-gray-200 w-full ${dropDown ? 'block' : 'hidden'}`}>
                    <ul className="py-2 text-sm" aria-labelledby="dropdownDefaultButton">
                        {discountData?.months.map((month, index) => (
                            <li key={index}>
                                <a
                                    onClick={(e) => { 
                                        e.preventDefault();
                                        setSelectedMonth(month); 
                                        setSelectedOption(null); 
                                        setDropDown(false); 
                                    }}
                                    href="#"
                                    className="block px-4 py-2.5 hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer"
                                >
                                    {month.time} muaj
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Entry Options Dropdown for Basic Membership */}
            {discountData.entries === "variable" && selectedMonth?.options && (
                <div className="flex flex-col gap-2 rounded-xl relative w-full">
                    <button
                        onClick={() => setOptionsDropDown(!optionsDropDown)}
                        className="text-blue-700 bg-blue-50 border border-blue-200 font-semibold rounded-lg text-sm px-5 py-2.5 text-center inline-flex items-center justify-between w-full hover:bg-blue-100 transition-colors"
                        type="button"
                    >
                        <span>{selectedOption ? `${selectedOption.entries} hyrje - ${selectedOption.cost} €` : "Zgjidh opsionin"}</span>
                        <svg className="w-2.5 h-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 6">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 4 4 4-4" />
                        </svg>
                    </button>

                    <div className={`z-10 bg-white text-black top-12 absolute divide-y divide-gray-100 rounded-lg shadow-lg border border-gray-200 w-full ${optionsDropDown ? 'block' : 'hidden'}`}>
                        <ul className="py-2 text-sm">
                            {selectedMonth.options.map((option, index) => (
                                <li key={index}>
                                    <a
                                        onClick={(e) => { 
                                            e.preventDefault();
                                            setSelectedOption(option); 
                                            setOptionsDropDown(false); 
                                        }}
                                        href="#"
                                        className="block px-4 py-2.5 hover:bg-blue-50 hover:text-blue-700 transition-colors cursor-pointer"
                                    >
                                        {option.entries} hyrje - {option.cost} €
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <div className="flex flex-col gap-4 pt-2">
                {/* Display entries - only show if option is selected for variable entries, or if month is selected for unlimited */}
                {(discountData.entries === "variable" && selectedOption) || (discountData.entries !== "variable" && selectedMonth) ? (
                    <div className="flex items-center gap-2">
                        <span className="text-gray-600 font-medium">Hyrjet:</span>
                        <span className="text-gray-800 font-semibold text-lg">
                            {selectedOption ? selectedOption.entries : (calculateEntries(selectedMonth.time) || "unlimited")}
                        </span>
                    </div>
                ) : null}
                
                {/* Price display with discount */}
                {(() => {
                    // For variable entries (basic), require selectedOption to show price
                    if (discountData.entries === "variable" && !selectedOption) {
                        return (
                            <div className="text-gray-500 text-sm italic">
                                Zgjidhni një plan për të parë çmimin
                            </div>
                        );
                    }
                    
                    const currentCost = selectedOption ? selectedOption.cost : selectedMonth?.cost;
                    const isPremium = discountData.entries === "unlimited";
                    const originalData = isPremium 
                        ? originalPrices.premium.months.find(m => m.time === selectedMonth.time)
                        : originalPrices.basic.months.find(m => m.time === selectedMonth.time);
                    
                    let originalCost = currentCost;
                    if (isPremium && originalData) {
                        originalCost = originalData.cost;
                    } else if (!isPremium && originalData && selectedOption) {
                        const originalOption = originalData.options?.find(o => o.entries === selectedOption.entries);
                        if (originalOption) originalCost = originalOption.cost;
                    }
                    
                    const discount = originalCost > currentCost && currentCost > 0
                        ? Math.round(((originalCost - currentCost) / originalCost) * 100)
                        : 0;
                    
                    // Only show pricing if we have valid costs
                    if (!currentCost || currentCost === 0) {
                        return (
                            <div className="text-gray-500 text-sm italic">
                                Zgjidhni një plan për të parë çmimin
                            </div>
                        );
                    }
                    
                    return (
                        <div className="flex flex-col gap-2 bg-gray-50 rounded-lg p-4 border border-gray-200">
                            {discount > 0 && (
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-gray-500 text-sm line-through">Çmimi i vjetër</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-500 text-sm line-through font-medium">{originalCost}€</span>
                                        <span className="bg-black-600 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-md">
                                            -{discount}%
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center justify-between">
                                <span className="text-gray-700 font-semibold">Çmimi</span>
                                <span className="text-black-600 font-bold text-2xl">{currentCost}€</span>
                            </div>
                        </div>
                    );
                })()}
                {/* <span
  style={{
    display: "inline-block",

    fontSize: "18px",
    fontWeight: "bold",
    color: "black",
    textTransform: "uppercase", // Uppercase for emphasis
    padding: "5px 10px",
    borderRadius: "6px",
    textAlign: "center",
    letterSpacing: "1px",
    backgroundClip: "text",
   
    textShadow: "1px 1px 2px rgba(0, 0, 0, 0.2)",
  }}
>
  <b style={{ color: "black", fontSize: "14px" }}>Ofertat për FUNDVIT - </b>
  <b style={{ color: "blue", fontSize: "16px" }}>
    Zbritje deri në 30%
  </b>
</span>  */}
             
                {/* Only show button if option is selected for variable entries, or if month is selected for unlimited */}
                {((discountData.entries === "variable" && selectedOption) || (discountData.entries !== "variable" && selectedMonth)) && (
                    <button 
                        onClick={handleChosePlan}
                        className={`w-full border-2 font-bold rounded-xl py-3 px-4 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02]
                            ${isSelected() ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-blue-600 text-blue-600 hover:bg-blue-50'}`}
                    >
                        {isSelected() ? '✓ Plani i Zgjedhur' : 'Zgjidh Planin'}
                    </button>
                )}
            </div>
        </div>
    );
};
